/**
 * Pair-up suggestions — "Sunita → Surya", and nothing more elaborate than that.
 *
 * The client was specific about the shape: a list a teacher reads out in five
 * minutes at the end of a lesson, and that parents can carry on with at home.
 * So it is names, in a row, with the questions the two of them are meant to sit
 * down over — not a matrix, not a drag-and-drop board, and not a per-question
 * table that makes a teacher work out who that leaves paired with whom.
 *
 * The questions too much of the class got wrong are lifted OUT of the list and
 * shown on their own: there are not enough people who solved those to go round,
 * and pairing on them quietly produces pairs where neither child knows it.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import type { PairingPlan } from '@cwy/config/contracts';

export function PeerPairs({
  paperId,
  classroomId,
  onOpenStudent,
}: {
  paperId: string;
  classroomId: string;
  onOpenStudent: (studentId: string) => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['peer-pairing', paperId, classroomId],
    queryFn: () =>
      apiGet<PairingPlan>(
        `/teacher/papers/${paperId}/peer-pairing?classroomId=${encodeURIComponent(classroomId)}`,
      ),
  });

  if (isLoading) {
    return (
      <div
        className="h-24 animate-pulse rounded-2xl"
        style={{ background: 'var(--surface-sunk)' }}
      />
    );
  }
  if (isError || !data) return null;

  const { pairs, reteachQuestions, unpaired } = data;

  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)' }}
    >
      <div
        className="px-4 py-3"
        style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--paper-sunk)' }}
      >
        <p className="text-sm font-bold" style={{ color: 'var(--text-body)' }}>
          Pair-up suggestions
        </p>
        <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {pairs.length
            ? `${pairs.length} pairs for five minutes at the end of the lesson. Read them out — they work at home too.`
            : 'Nothing to pair on this paper.'}
        </p>
      </div>

      {reteachQuestions.length > 0 && (
        <div
          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5"
          style={{
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--orange-bg)',
          }}
        >
          <span
            className="text-fs-11 font-bold uppercase tracking-wide"
            style={{ color: 'var(--orange-deep)' }}
          >
            Re-teach, don't pair
          </span>
          <span className="text-xs" style={{ color: 'var(--ink-2)' }}>
            {reteachQuestions
              .map((q) => `Q${q.qNumber} ${q.subtopicName} (${q.orangePercent}% Orange)`)
              .join(' · ')}
          </span>
        </div>
      )}

      <ul>
        {pairs.map((pair) => (
          <li
            key={pair.learnerId}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 py-2.5"
            style={{ borderTop: '1px solid var(--border-subtle)' }}
          >
            <button
              type="button"
              onClick={() => onOpenStudent(pair.learnerId)}
              className="text-fs-13 font-bold underline-offset-2 hover:underline"
              style={{ color: 'var(--text-strong)' }}
            >
              {pair.learnerName}
            </button>

            <span aria-label="is helped by" style={{ color: 'var(--ink-3)' }}>
              →
            </span>

            <button
              type="button"
              onClick={() => onOpenStudent(pair.mentorId)}
              className="text-fs-13 font-bold underline-offset-2 hover:underline"
              style={{ color: 'var(--green-text)' }}
            >
              {pair.mentorName}
            </button>

            <span className="text-xs" style={{ color: 'var(--ink-2)' }}>
              {pair.questions.map((q) => `Q${q.qNumber} ${q.subtopicName}`).join(' · ')}
            </span>

            {pair.uncovered.length > 0 && (
              <span className="text-fs-11" style={{ color: 'var(--ink-3)' }}>
                (Q{pair.uncovered.map((q) => q.qNumber).join(', Q')} still needs you)
              </span>
            )}
          </li>
        ))}
      </ul>

      {unpaired.length > 0 && (
        <p
          className="px-4 py-2.5 text-xs"
          style={{ borderTop: '1px solid var(--border-subtle)', color: 'var(--ink-2)' }}
        >
          <span className="font-bold">Nobody to pair with:</span>{' '}
          {unpaired.map((u) => `${u.studentName} (${u.questions})`).join(' · ')} — nobody in the
          class solved these first time, so they are yours.
        </p>
      )}
    </div>
  );
}
